import { Injectable, inject } from '@angular/core';
import {
  ILoadingVerificationHistoryResponse,
  ILoadingVerificationListQuery,
  ILoadingVerificationListResponse,
  ILoadingVerificationRequest,
  ILoadingVerificationResponse,
  ITransportUnitOcrRequest,
  ITransportUnitOcrResponse,
} from '@cargo-cockpit/types';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { ApiService } from '../api/api.service';
import { SystemConfigurationItem } from '../report/report.service';

/** SystemConfiguration type holding the loading verification settings. */
const LOADING_VERIFICATION_CONFIG_TYPE = 'LOADING_VERIFICATION_SETTINGS';

/** Item within it holding the comma-separated beta cohort. */
const BETA_USERS_ITEM_ID = 'BETA_USERS';

/**
 * Client for the bulk loading verification endpoints (CYLP-542).
 *
 * The backend is the only authority on a verdict: every comparison — check-digit, match
 * against the checked-in unit, match against the assigned tank — happens there, against
 * data this client never sees. The client's job is to collect scans and render the answer.
 * Nothing here decides pass or fail, and it must stay that way: a client-side verdict could
 * be bypassed from the device, which defeats the point of the check.
 *
 * @see `packages/backend/yard-access/lib/handlers/http/loading-verification.ts`
 */
@Injectable({ providedIn: 'root' })
export class LoadingVerificationService {
  private readonly apiService = inject(ApiService);

  /**
   * Verify what has been scanned so far.
   *
   * Progressive by design: post only the legs that have input and the response carries a
   * verdict for exactly those. That is what lets the operator see leg B fail the moment
   * they scan the container, rather than after all three scans.
   *
   * @param commit When false (the default), nothing is written to the audit table. Pass
   *   true exactly once, when the attempt reaches a terminal verdict — otherwise every
   *   intermediate check becomes its own audit record and the trail becomes unreadable.
   */
  verify(
    freightOrderId: string,
    request: ILoadingVerificationRequest,
  ): Observable<ILoadingVerificationResponse> {
    return this.apiService.post<ILoadingVerificationRequest, ILoadingVerificationResponse>(
      `/yard-access/${encodeURIComponent(freightOrderId)}/loading-verification`,
      {
        // Stamped here rather than in the caller so every request carries one. The server
        // keeps it separate from its own `performedAt`; the gap between them is the only
        // way to spot a device with a wrong clock or a scan that sat in a queue.
        clientTimestamp: new Date().toISOString(),
        ...request,
      },
    );
  }

  /**
   * Read the container number off a photo of the transport unit (leg B).
   *
   * A separate endpoint and a separate service from {@link verify}, because this is a
   * *capture* rather than a verification: a photo too dark to read is not a failed check and
   * must not produce an audit record — the operator has not asserted anything yet.
   *
   * The result is advisory. Whatever the operator confirms is submitted through
   * {@link verify} with `transportUnitSource: 'ocr'`, and the verdict still comes from the
   * backend comparison against the check-in record. Never auto-submit the top candidate: a
   * photo at a yard can catch a neighbouring container whose number is also check-digit
   * valid, so the operator has to be the one who says which unit is in front of them.
   */
  recognizeTransportUnit(frame: {
    base64: string;
    contentType?: 'image/jpeg' | 'image/png';
  }): Observable<ITransportUnitOcrResponse> {
    return this.apiService.post<ITransportUnitOcrRequest, ITransportUnitOcrResponse>(
      '/loading-verification/transport-unit-ocr',
      {
        imageBase64: frame.base64,
        contentType: frame.contentType,
        clientTimestamp: new Date().toISOString(),
      },
    );
  }

  /**
   * Audit history for one freight order, newest first.
   *
   * Includes failed attempts. During an incident review the useful question is "what did we
   * reject, and who carried on anyway", which only the failures can answer.
   */
  getHistory(
    freightOrderId: string,
    limit?: number,
  ): Observable<ILoadingVerificationHistoryResponse> {
    return this.apiService.get<ILoadingVerificationHistoryResponse>(
      `/yard-access/${encodeURIComponent(freightOrderId)}/loading-verification`,
      { limit },
    );
  }

  /**
   * Records across one or more bays in a time window, newest first, with key figures.
   *
   * `locations` is required and sent as `group#location` keys — the same composite form the
   * user profile stores assignments in. The backend has no "all bays" mode: the only index
   * that can answer a time-window question is partitioned per bay, so the caller has to say
   * which bays it means.
   *
   * `from`/`to` are full ISO instants, not dates, and the caller owns the conversion. A bay in
   * Turkey and a browser in Germany disagree about where "today" starts, and the user means
   * their own day — so the day boundaries are computed where the user's clock is.
   */
  list(query: ILoadingVerificationListQuery): Observable<ILoadingVerificationListResponse> {
    return this.apiService.get<ILoadingVerificationListResponse>(
      '/yard-access/loading-verifications',
      {
        locations: query.locations.join(','),
        from: query.from,
        to: query.to,
        freightOrder: query.freightOrder,
        overall: query.overall,
        limit: query.limit,
      },
    );
  }

  /**
   * CWIDs admitted to the beta, as configured in System Settings (CYLP-542).
   *
   * Used to hide the menu item and guard the routes. It is a *courtesy* gate, not the real
   * one — the backend performs the same check on every request and is the only authority.
   * Treat this purely as "do not show someone a page that will refuse them".
   *
   * An error yields an empty list, which hides the feature. A UI that cannot find out whether
   * you are in the pilot should not advertise the pilot; the alternative is showing a menu
   * item that leads to a 403.
   */
  getBetaUsers(): Observable<string[]> {
    return this.apiService
      .get<SystemConfigurationItem[]>(`/system-configurations/${LOADING_VERIFICATION_CONFIG_TYPE}`)
      .pipe(
        map((items) => {
          const item = (items ?? []).find((candidate) => candidate.itemId === BETA_USERS_ITEM_ID);

          return (item?.itemValue ?? '')
            .split(',')
            .map((cwid) => cwid.trim())
            .filter((cwid) => cwid.length > 0);
        }),
        catchError((error) => {
          console.error('Error fetching the loading verification beta user list:', error);
          return of<string[]>([]);
        }),
      );
  }

  /**
   * Replace the beta cohort. Admin/TechAdmin only, enforced by the backend.
   *
   * Falls back to `POST` when the `PUT` 404s, because `updateConfigurationHandler` refuses to
   * create: it 404s when the item does not exist. Without the fallback this dialog would only
   * work in an environment where the system-configuration seed had been run, and a new
   * environment's first pilot user could not be added through the UI at all.
   */
  saveBetaUsers(cwids: string[]): Observable<void> {
    const itemValue = cwids
      .map((cwid) => cwid.trim())
      .filter((cwid) => cwid.length > 0)
      .join(',');

    return this.apiService
      .put<
        { itemValue: string },
        SystemConfigurationItem
      >(`/system-configurations/${LOADING_VERIFICATION_CONFIG_TYPE}/${BETA_USERS_ITEM_ID}`, { itemValue })
      .pipe(
        catchError((error: unknown) => {
          const status = (error as { status?: number })?.status;

          if (status !== 404) {
            return throwError(() => error);
          }

          return this.apiService.post<SystemConfigurationItem, SystemConfigurationItem>(
            `/system-configurations/${LOADING_VERIFICATION_CONFIG_TYPE}`,
            {
              configType: LOADING_VERIFICATION_CONFIG_TYPE,
              itemId: BETA_USERS_ITEM_ID,
              itemValue,
              name: 'Loading Verification Beta Users',
              description:
                'Comma-separated CWIDs admitted to the bulk loading verification beta. ' +
                'Empty means no access for everyone except Admins.',
            },
          );
        }),
        map(() => undefined),
      );
  }
}
